import React from "react";

export default function FileCompressor() {
  return (
    <div className="max-w-2xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-2 flex items-center justify-between gap-2">
        File Compressor
      </h1>
      <div className="mb-2 text-gray-700">
        <p>
          This project implements the LZW compression algorithm in C. It
          required implementing custom data structures (e.g. a string to
          compression-code dictionary) and bit-packing utilities.
        </p>
      </div>
      <span className="inline-flex flex-wrap mb-8 gap-1">
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
          C
        </span>
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
          Data Structures
        </span>
      </span>
      <div className="mb-6">
        <h2 className="font-semibold text-base mb-1">Features</h2>
        <ul className="list-disc ml-6 text-gray-700">
          <li>LZW compression & decompression</li>
          <li>Variable-length codes (9–20 bits, default 12)</li>
          <li>Optional pruning of unused codes</li>
          <li>Efficient bit-packing</li>
          <li>Custom dictionary and bit utilities in C</li>
        </ul>
      </div>
      <div className="mb-6">
        <h2 className="font-semibold text-base mb-1">Usage</h2>
        <pre className="bg-gray-100 rounded p-3 text-sm overflow-x-auto mb-2">
          {`make
cat file_to_compress.txt | ./encode > compressed.Z
cat compressed.Z | ./decode`}
        </pre>
      </div>
      <div className="mb-6">
        <h2 className="font-semibold text-base mb-1">
          Stages of Implementation
        </h2>
        <ul className="list-disc ml-6 text-gray-700">
          <li>
            <b>Stage 1:</b> Static 12-bit encoding, no pruning.
          </li>
          <li>
            <b>Stage 2:</b> Variable-length (9-20 bit) encoding, with optional
            pruning.
          </li>
          <li>
            <b>Stage 3:</b> Adds bit-packing for efficient output.
          </li>
        </ul>
        <p className="text-gray-500 text-sm mt-2">
          By default the program runs in stage 3, since this stage implements
          the complete, efficient compression algorithm. You can, however,
          select another stage using the STAGE environment variable
        </p>
      </div>
      <div className="mb-6">
        <h2 className="font-semibold text-base mb-1">Performance</h2>
        <p className="text-gray-700 text-sm mb-2">
          Every cell below is the difference between my program's compression
          rate and the macOS default; lower means mine compresses more. Each
          column is for a different maximum code size: 12, 14, and 16 bits.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left border border-gray-200 overflow-x-scroll">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-2 py-1 border">File</th>
                <th className="px-2 py-1 border">12 bits</th>
                <th className="px-2 py-1 border">14 bits</th>
                <th className="px-2 py-1 border">16 bits</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border px-2 py-1">alice29.txt</td>
                <td className="border px-2 py-1">-2.13</td>
                <td className="border px-2 py-1">-1.84</td>
                <td className="border px-2 py-1">-0.03</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">asyoulik.txt</td>
                <td className="border px-2 py-1">-2.46</td>
                <td className="border px-2 py-1">0.09</td>
                <td className="border px-2 py-1">-0.03</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">book1</td>
                <td className="border px-2 py-1">-2.47</td>
                <td className="border px-2 py-1">-2.64</td>
                <td className="border px-2 py-1">-2.09</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">fields.c</td>
                <td className="border px-2 py-1">-0.07</td>
                <td className="border px-2 py-1">-0.07</td>
                <td className="border px-2 py-1">-0.07</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">kennedy.xls</td>
                <td className="border px-2 py-1">-4.27</td>
                <td className="border px-2 py-1">-0.61</td>
                <td className="border px-2 py-1">-0.07</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">obj1</td>
                <td className="border px-2 py-1">-13.37</td>
                <td className="border px-2 py-1">-0.01</td>
                <td className="border px-2 py-1">-0.01</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">alphabet.txt</td>
                <td className="border px-2 py-1">0.00</td>
                <td className="border px-2 py-1">0.00</td>
                <td className="border px-2 py-1">0.00</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">full_essay</td>
                <td className="border px-2 py-1">-6.03</td>
                <td className="border px-2 py-1">-0.03</td>
                <td className="border px-2 py-1">-0.03</td>
              </tr>
              <tr>
                <td className="border px-2 py-1">pi.txt</td>
                <td className="border px-2 py-1">-0.61</td>
                <td className="border px-2 py-1">-0.58</td>
                <td className="border px-2 py-1">-0.64</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
